/**
 * Circuit Breaker pour les services API
 * Surveille la santé des services et gère les défaillances
 */

import debug from '@commons-shared/debug.js';
import { config } from '../config/environment.js';
import { get } from './api-router.js';
import { CIRCUIT_STATES } from '../constants/circuit-breaker-constants.js';
import { ROUTES_CONFIG, getRouteConfigByRoute, DEFAULT_CIRCUIT_BREAKER_CONFIG } from '../config/routes-config.js';
import { getItemEncrypted } from './encrypted-local-storage.js';
import { getRoutingConfig } from './routing-config-manager.js';

/**
 * Modes de vérification de santé
 */
export const HEALTHCHECK_MODE = {
  USER: 'USER',
  INTERNAL: 'INTERNAL'
};

/**
 * Stratégies de vérification de santé
 */
export const HEALTHCHECK_STRATEGY = {
  ON_CALLS: 'onCalls',      
  ON_INTERVALS: 'onIntervals'
};

/**
 * Registre global des circuit breakers
 */
const circuitBreakers = new Map();

/**
 * Flag pour indiquer si les circuit-breakers sont actuellement arrêtés
 */
let areCircuitBreakersStopped = false;

/**
 * Observateurs globaux pour notifier les changements d'état "stopped"
 */
const stoppedStateObservers = new Set();

/**
 * Classe Circuit Breaker
 */
class CircuitBreaker {
  constructor(route, routeConfig = {}) {
    this.route = route;
    this.config = { ...DEFAULT_CIRCUIT_BREAKER_CONFIG, ...routeConfig };
    this.state = CIRCUIT_STATES.CLOSED;
    this.failures = 0;
    this.lastFailureTime = null;
    this.healthCheckIntervalId = null;
    this.isHealthy = true;
    this.lastHealthCheck = null;
    this.errorType = null;
    this.errorMessage = null;
    this.apiStatus = null;
    this.listeners = new Set();
    
    // Watchdog properties
    this.callHistory = [];
    this.watchdogTriggered = false;
    this.lastWatchdogTrigger = null;
    this.watchdogTimer = null; // Timer pour la réinitialisation automatique
    this.watchdogTimer = null; // Timer pour la réinitialisation automatique

    // Démarrer la surveillance de santé
    this.startHealthMonitoring();
  }

  /**
   * Ajouter un écouteur pour les changements d'état
   */
  addListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notifier les écouteurs des changements d'état
   */
  notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback(this.getStatus());
      } catch (error) {
        debug.error(config, 'Erreur dans un listener de circuit breaker:', error);
      }
    });
  }

  /**
   * Obtenir le statut actuel du circuit breaker
   */
  getStatus() {
    return {
      route: this.route,
      state: this.state,
      isHealthy: this.isHealthy,
      failures: this.failures,
      lastHealthCheck: this.lastHealthCheck,
      errorType: this.errorType,
      errorMessage: this.errorMessage,
      apiStatus: this.apiStatus,
      watchdogTriggered: this.watchdogTriggered,
      lastWatchdogTrigger: this.lastWatchdogTrigger,
      isStopped: areCircuitBreakersStopped
    };
  }

  /**
   * Vérifier le statut du watchdog sans enregistrer un nouvel appel
   * Utilisé pour les health checks automatiques qui ne doivent pas interférer avec le watchdog
   * @returns {boolean} true si le watchdog est actif et bloque les requêtes
   */
  isWatchdogActive() {
    if (!this.config.watchdogEnabled || !this.watchdogTriggered) {
      return false;
    }

    const now = Date.now();
    
    // Vérifier si on est encore en période de cooldown
    if (this.lastWatchdogTrigger) {
      const timeSinceLastTrigger = now - this.lastWatchdogTrigger;
      if (timeSinceLastTrigger < this.config.watchdogCooldown) {
        return true; // Watchdog toujours actif
      } else {
        // Fin du cooldown - réinitialiser complètement le watchdog
        debug.log(config, `Fin du cooldown watchdog pour ${this.route} - réinitialisation automatique`);
        this.resetWatchdog();
        
        // Réinitialiser aussi l'errorType si c'était du watchdog
        if (this.errorType === 'watchdog') {
          this.errorType = null;
          this.errorMessage = null;
          // Notifier le changement d'état
          this.notifyListeners();
        }
        return false;
      }
    }
    
    return false;
  }

  /**
   * Enregistrer un appel et vérifier le watchdog
   * Cette méthode doit être appelée à chaque requête vers la route
   */
  recordCall() {
    if (!this.config.watchdogEnabled) {
      return false;
    }

    // Vérifier d'abord si le watchdog est déjà actif (sans l'enregistrer)
    if (this.isWatchdogActive()) {
      return true; // Watchdog actif, bloquer la requête
    }

    const now = Date.now();
    
    // Nettoyer l'historique des appels anciens
    this.callHistory = this.callHistory.filter(
      timestamp => now - timestamp <= this.config.watchdogTimeWindow
    );
    
    // Ajouter l'appel actuel
    this.callHistory.push(now);
    
    // Vérifier si le seuil est dépassé
    if (this.callHistory.length >= this.config.watchdogThreshold) {
      if (!this.watchdogTriggered) {
        debug.warn(config, `Watchdog déclenché pour ${this.route}: ${this.callHistory.length} appels en ${this.config.watchdogTimeWindow}ms`);
        this.triggerWatchdog();
      }
      return true; // Watchdog déclenché
    }
    
    return false; // Pas de problème détecté
  }

  /**
   * Déclencher le watchdog
   */
  triggerWatchdog() {
    this.watchdogTriggered = true;
    this.lastWatchdogTrigger = Date.now();
    
    // Programmer la réinitialisation automatique du watchdog après le cooldown
    if (this.watchdogTimer) {
      clearTimeout(this.watchdogTimer);
    }
    
    this.watchdogTimer = setTimeout(() => {
      debug.warn(config, `🔥 Timer automatique : fin du cooldown watchdog pour ${this.route} - réinitialisation automatique`);
      this.resetWatchdog();
      
      // Réinitialiser aussi l'errorType si c'était du watchdog
      if (this.errorType === 'watchdog') {
        this.errorType = null;
        this.errorMessage = null;
        // Notifier le changement d'état
        this.notifyListeners();
      }
    }, this.config.watchdogCooldown);
    
    // Forcer l'ouverture immédiate du circuit pour le watchdog
    const previousState = this.state;
    const previousHealth = this.isHealthy;
    
    this.failures++;
    this.lastFailureTime = new Date();
    this.errorType = 'watchdog';
    this.errorMessage = `${this.callHistory.length} appels rapides détectés en ${this.config.watchdogTimeWindow}ms`;
    this.lastHealthCheck = new Date();
    this.isHealthy = false;
    this.state = CIRCUIT_STATES.OPEN; // Ouverture immédiate pour le watchdog
    
    debug.error(config, `🔥 Watchdog activé pour ${this.route}: circuit ouvert immédiatement (${this.failures} échecs, seuil ignoré)`);
    
    // Notifier les changements d'état
    if (previousState !== this.state || previousHealth !== this.isHealthy) {
      this.notifyListeners();
    }
  }

  /**
   * Réinitialiser le watchdog pour cette instance de circuit breaker
   */
  resetWatchdog() {
    this.callHistory = [];
    this.watchdogTriggered = false;
    this.lastWatchdogTrigger = null;
    
    // Nettoyer le timer automatique s'il existe
    if (this.watchdogTimer) {
      clearTimeout(this.watchdogTimer);
      this.watchdogTimer = null;
    }
    
    debug.log(config, `Watchdog réinitialisé pour ${this.route}`);
  }

  /**
   * Démarrer la surveillance de santé
   */
  startHealthMonitoring() {
    // Délai initial avant la première vérification pour laisser le temps à l'initialisation
    setTimeout(() => {
      this.checkHealth();
    }, 2000); // 2 secondes de délai

    // Vérifications périodiques
    this.healthCheckIntervalId = setInterval(() => {
      this.checkHealth();
    }, this.config.healthCheckInterval);

    debug.log(config, `Circuit breaker démarré pour ${this.route} (première vérification dans 2s)`);
  }

  /**
   * Arrêter la surveillance de santé
   */
  stopHealthMonitoring() {
    if (this.healthCheckIntervalId) {
      clearInterval(this.healthCheckIntervalId);
      this.healthCheckIntervalId = null;
    }
    debug.log(config, `Circuit breaker arrêté pour ${this.route}`);
  }

  /**
   * Vérifier la santé du service
   * Gère trois niveaux d'erreur:
   * 1. Le worker n'est pas accessible (problème de connectivité)
   * 2. Le worker est accessible mais répond avec une erreur (service inaccessible)
   * 3. Les credentials sont refusés (validation échouée)
   */
  async checkHealth(trigger = HEALTHCHECK_MODE.INTERNAL) {
    // Si CHECKHEALTH_MODE est USER, checkhealth() est exécuté dans tous les cas
    if (trigger === HEALTHCHECK_MODE.USER) {
      debug.log(config, `Vérification de santé forcée par l'utilisateur pour ${this.route}`);
      return this.performHealthCheck(trigger);
    }

    // Logique conditionnelle basée sur l'état du circuit et les stratégies
    switch (this.state) {
      case CIRCUIT_STATES.OPEN:
        // Si le circuit est OPEN
        if (this.config.checkHealthStrategy === HEALTHCHECK_STRATEGY.ON_INTERVALS) {
          // Fonctionnement actuel - exécuter le check
          return this.performHealthCheck(trigger);
        } else if (this.config.checkHealthStrategy === HEALTHCHECK_STRATEGY.ON_CALLS) {
          // Ignorer le checkhealth
          debug.log(config, `Check health ignoré pour ${this.route} (circuit OPEN, stratégie ON_CALLS)`);
          return;
        }
        break;

      case CIRCUIT_STATES.CLOSED:
        // Si le circuit est CLOSED
        if (this.config.checkHealthStrategy === HEALTHCHECK_STRATEGY.ON_INTERVALS) {
          // Fonctionnement actuel - exécuter le check
          return this.performHealthCheck(trigger);
        } else if (this.config.checkHealthStrategy === HEALTHCHECK_STRATEGY.ON_CALLS) {
          // Ignorer le checkhealth
          debug.log(config, `Check health ignoré pour ${this.route} (circuit CLOSED, stratégie checkHealth ON_CALLS)`);
          return;
        }
        break;

      case CIRCUIT_STATES.HALF_OPEN:
        // Si le circuit passe en HALF_OPEN
        if (this.config.recoveryStrategy === HEALTHCHECK_STRATEGY.ON_INTERVALS) {
          // Fonctionnement actuel - exécuter le check
          return this.performHealthCheck(trigger);
        } else if (this.config.recoveryStrategy === HEALTHCHECK_STRATEGY.ON_CALLS) {
          // Le checkhealth est réalisé
          return this.performHealthCheck(trigger);
        }
        break;

      default:
        // État inconnu, exécuter par sécurité
        return this.performHealthCheck(trigger);
    }
  }

  /**
   * Effectuer la vérification de santé effective
   */
  async performHealthCheck(trigger) {
    try {
      const healthEndpoint = `${this.route}/health`;
      const accessEndpoint = `${this.route}/check-access`;
      
      // Déterminer le mode health check à passer à l'API router
      const healthCheckMode = trigger; // 'USER' ou 'INTERNAL'

      debug.log(config, `Vérification de santé pour ${this.route} (trigger: ${trigger}, healthCheckMode: ${healthCheckMode})...`);

      // Vérifier d'abord la santé générale
      let healthResponse;
      try {
        healthResponse = await Promise.race([
          get(healthEndpoint, {}, false, healthCheckMode),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Health check timeout')), this.config.timeout)
          )
        ]);
      } catch (error) {
        // Le worker n'est pas accessible (cas 1)
        this.onFailure('connectivity', `Service unavailable: ${error.message}`);
        return;
      }

      if (!healthResponse || !healthResponse.success) {
        // Le worker a répondu mais avec une erreur (cas 2)
        const errorType = healthResponse?.errorType || 'service';
        const message = healthResponse?.message || 'Health check failed';
        this.onFailure(errorType, message);
        return;
      }

      // Vérifier ensuite l'accès
      let accessResponse;
      try {
        accessResponse = await Promise.race([
          this.checkAccess(healthCheckMode),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Access check timeout')), this.config.timeout)
          )
        ]);
      } catch (error) {
        // Le worker n'est pas accessible pour le check d'accès (cas 1)
        this.onFailure('connectivity', `Service unavailable: ${error.message}`);
        return;
      }

      if (!accessResponse || !accessResponse.success) {
        // Le worker a répondu mais l'accès a échoué (cas 2 ou 3)
        const errorType = accessResponse?.errorType || 'auth';
        const message = accessResponse?.message || 'Access check failed';
        this.onFailure(errorType, message);
        return;
      }

      // Service opérationnel
      this.onSuccess();

    } catch (error) {
      // Erreur générale non gérée (cas 1 par défaut)
      debug.error(config, `Échec de la vérification de santé pour ${this.route}:`, error);
      this.onFailure('connectivity', error.message || 'Unknown error during health check');
    }
  }

  /**
   * Analyser les headers de réponse et mettre à jour l'état du circuit
   * @param {Headers|Object} responseHeaders - Headers de la réponse HTTP
   */
  analyzeResponseHeaders(responseHeaders) {
    // Récupérer l'état du circuit depuis le header X-API-Circuit-State
    const circuitState = responseHeaders?.get?.('X-API-Circuit-State') || 
                        responseHeaders?.['X-API-Circuit-State'] || 
                        null;

    // Récupérer le code de statut API depuis le header X-API-Status
    const apiStatus = responseHeaders?.get?.('X-API-Status') || 
                     responseHeaders?.['X-API-Status'] || 
                     null;

    // Mettre à jour l'état du circuit breaker seulement si des headers sont présents
    if (circuitState) {
      this.updateCircuitState(circuitState, apiStatus);
    }
  }

  /**
   * Vérifier l'accès au service
   */
  async checkAccess(healthCheckMode = null) {
    // Pour les routes IA, vérifier d'abord si une clé API est disponible
    if (this.route.startsWith('/api/ia/')) {
      const routeConfig = getRouteConfigByRoute(this.route);
      if (routeConfig && routeConfig.keyName) {
        try {
          // Utiliser la nouvelle structure JSON routing_config
          // Les sections IA sont mappées: text_ai_api_key -> routing_config.text
          const sectionMap = {
            'text_ai_api_key': 'text',
            'vision_ai_api_key': 'vision',
            'translation_ai_api_key': 'translation'
          };
          
          const section = sectionMap[routeConfig.keyName];
          if (!section) {
            debug.warn(config, `Section inconnue pour ${routeConfig.keyName}`);
            return {
              success: false,
              errorType: 'auth',
              message: `Configuration API incorrecte pour ${routeConfig.displayName}.`
            };
          }

          // Utiliser getRoutingConfig pour éviter les dépendances circulaires
          const routingConfig = await getRoutingConfig();
          
          const encryptedKey = routingConfig[section]?.['X-API-Key'];
          if (!encryptedKey) {
            debug.warn(config, `Aucune clé API configurée pour ${this.route} (section: ${section})`);
            return {
              success: false,
              errorType: 'auth',
              message: `Clé API ${routeConfig.displayName} non configurée. Veuillez configurer votre clé API dans les paramètres.`
            };
          }
        } catch (error) {
          debug.error(config, `Erreur lors de la vérification de la clé API pour ${this.route}:`, error);
          return {
            success: false,
            errorType: 'auth',
            message: `Impossible de vérifier la clé API ${routeConfig.displayName}. Veuillez vérifier votre configuration.`
          };
        }
      }
    }

    const endpoint = `${this.route}/check-access`;

    try {
      const response = await get(endpoint, {}, false, healthCheckMode);

      // Analyser les headers de la réponse (si disponibles)
      // Note: les headers sont déjà analysés dans routedFetch via analyzeResponseHeaders
      
      // Retourner la réponse telle quelle si elle contient déjà les informations d'erreur
      if (response && typeof response === 'object') {
        return response;
      }

      // Si la réponse n'est pas structurée, considérer comme un succès
      return { success: true };

    } catch (error) {
      debug.error(config, `Erreur lors de la vérification d'accès pour ${this.route}:`, error);
      
      // En cas d'erreur, forcer l'état OPEN
      this.updateCircuitState(CIRCUIT_STATES.OPEN, null);
      
      // Distinguer les erreurs de connectivité des autres erreurs
      if (error.name === 'TypeError' || error.message.includes('fetch') || error.message.includes('network')) {
        return { 
          success: false, 
          errorType: 'connectivity', 
          message: 'Unable to connect to service for access check' 
        };
      }
      
      return { 
        success: false, 
        errorType: 'service', 
        message: error.message || 'Access check failed' 
      };
    }
  }

  /**
   * Mettre à jour l'état du circuit breaker selon les headers de l'API
   */
  updateCircuitState(circuitState, apiStatus) {
    const previousState = this.state;
    const previousHealth = this.isHealthy;

    // Vérifier si le watchdog est actif avant de modifier l'errorType
    const watchdogActive = this.watchdogTriggered && this.errorType === 'watchdog';

    // Mettre à jour l'état du circuit
    this.state = circuitState;
    this.apiStatus = apiStatus;
    this.lastHealthCheck = new Date();

    // Mettre à jour isHealthy selon l'état du circuit
    switch (circuitState) {
      case CIRCUIT_STATES.CLOSED:
        this.isHealthy = true;
        this.failures = 0;
        this.lastFailureTime = null;
        
        // NE PAS réinitialiser le watchdog automatiquement quand le circuit passe à CLOSED
        // Le watchdog doit respecter son cooldown même si l'API répond CLOSED
        // Il se réinitialisera automatiquement à la fin de son cooldown dans recordCall()
        
        // Réinitialiser errorType et errorMessage seulement si le watchdog n'est pas actif
        if (!this.watchdogTriggered || this.errorType !== 'watchdog') {
          this.errorType = null;
          this.errorMessage = null;
        }
        break;
      
      case CIRCUIT_STATES.HALF_OPEN:
        this.isHealthy = false;
        this.failures++; // Compter les HALF_OPEN reçus
        this.lastFailureTime = new Date();
        
        // Ne pas écraser l'errorType si le watchdog est actif
        if (!this.errorType && !watchdogActive) {
          this.errorType = 'api';
          this.errorMessage = `Circuit in HALF_OPEN state (API Status: ${apiStatus || 'unknown'})`;
        }
        break;
      
      case CIRCUIT_STATES.OPEN:
      default:
        this.isHealthy = false;
        this.failures++; // Compter les OPEN reçus
        this.lastFailureTime = new Date();
        
        // Ne pas écraser l'errorType si le watchdog est actif
        if (!this.errorType && !watchdogActive) {
          this.errorType = 'api';
          this.errorMessage = `Circuit forced to OPEN state (API Status: ${apiStatus || 'unknown'})`;
        }
        break;
    }

    // Notifier les listeners si l'état a changé
    if (previousState !== this.state || previousHealth !== this.isHealthy) {
      debug.log(config, `Circuit breaker pour ${this.route} mis à jour: ${previousState} -> ${this.state} (API Status: ${apiStatus || 'unknown'}, failures: ${this.failures})`);
      this.notifyListeners();
    }
  }

  /**
   * Gérer un succès
   */
  onSuccess() {
    // IMPORTANT : Log détaillé pour debugger le problème de cooldown
    const watchdogStatus = this.watchdogTriggered ? 'ACTIF' : 'INACTIF';
    if (this.watchdogTriggered) {
      const timeSinceWatchdog = Date.now() - this.lastWatchdogTrigger;
      debug.warn(config, `🔥 onSuccess() appelé pour ${this.route} - Watchdog: ${watchdogStatus}, Temps depuis déclenchement: ${timeSinceWatchdog}ms, ErrorType actuel: ${this.errorType}`);
    }

    this.failures = 0;
    this.lastFailureTime = null;
    this.apiStatus = null;
    this.lastHealthCheck = new Date();

    const previousState = this.state;
    const previousHealth = this.isHealthy;

    this.state = CIRCUIT_STATES.CLOSED;
    this.isHealthy = true;

    // NE PAS réinitialiser le watchdog en cas de succès !
    // Le watchdog doit respecter son cooldown même si l'API répond correctement
    // Il se réinitialisera automatiquement à la fin de son cooldown dans recordCall()

    // Réinitialiser errorType et errorMessage seulement si le watchdog n'est pas actif
    if (!this.watchdogTriggered || this.errorType !== 'watchdog') {
      this.errorType = null;
      this.errorMessage = null;
    } else {
      debug.warn(config, `🔥 Watchdog actif - CONSERVATION de l'errorType 'watchdog' malgré onSuccess() pour ${this.route}`);
    }

    // Notifier seulement si l'état a changé
    if (previousState !== this.state || previousHealth !== this.isHealthy) {
      debug.log(config, `Circuit breaker pour ${this.route} fermé (opérationnel)`);
      this.notifyListeners();
    }
  }

  /**
   * Gérer un échec
   */
  onFailure(errorType, errorMessage) {
    this.failures++;
    this.lastFailureTime = new Date();
    this.errorType = errorType;
    this.errorMessage = errorMessage;
    this.lastHealthCheck = new Date();

    const previousState = this.state;
    const previousHealth = this.isHealthy;

    this.isHealthy = false;

    if (this.failures >= this.config.failureThreshold) {
      this.state = CIRCUIT_STATES.OPEN;
      debug.error(config, `Circuit breaker pour ${this.route} ouvert après ${this.failures} échecs (${errorType}: ${errorMessage})`);
    }

    // Notifier seulement si l'état a changé
    if (previousState !== this.state || previousHealth !== this.isHealthy) {
      this.notifyListeners();
    }
  }

  /**
   * Vérifier si le circuit peut être testé pour récupération
   */
  canAttemptRecovery() {
    if (this.state !== CIRCUIT_STATES.OPEN) return false;
    if (!this.lastFailureTime) return false;

    const timeSinceLastFailure = Date.now() - this.lastFailureTime.getTime();
    return timeSinceLastFailure >= this.config.recoveryTimeout;
  }

  /**
   * Tenter une récupération
   */
  attemptRecovery() {
    if (!this.canAttemptRecovery()) return;

    debug.log(config, `Tentative de récupération du circuit breaker pour ${this.route}`);
    this.state = CIRCUIT_STATES.HALF_OPEN;
    this.notifyListeners();
    
    // Relancer immédiatement un health check
    setTimeout(() => this.checkHealth(), 100);
  }
}

/**
 * Flag pour indiquer si les circuit breakers sont initialisés
 */
let circuitBreakersInitialized = false;

/**
 * Créer ou récupérer un circuit breaker pour une route
 */
export function getCircuitBreaker(route, routeConfig = {}) {
  // Ne créer automatiquement que si les circuit breakers sont initialisés
  if (!circuitBreakers.has(route)) {
    if (!circuitBreakersInitialized) {
      // Si les circuit breakers ne sont pas encore initialisés, retourner null
      debug.log(config, `Circuit breaker pour ${route} non disponible - circuit breakers non initialisés`);
      return null;
    }
    const breaker = new CircuitBreaker(route, routeConfig);
    circuitBreakers.set(route, breaker);
  }
  return circuitBreakers.get(route);
}

/**
 * Vérifier si les circuit breakers sont initialisés
 */
export function areCircuitBreakersInitialized() {
  return circuitBreakersInitialized;
}

/**
 * S'abonner aux changements d'état "stopped"
 * @param {Function} observer - Fonction appelée quand l'état change
 * @returns {Function} - Fonction pour se désabonner
 */
export function addStoppedStateObserver(observer) {
  stoppedStateObservers.add(observer);
  return () => stoppedStateObservers.delete(observer);
}

/**
 * Notifier tous les observateurs du changement d'état "stopped"
 */
function notifyStoppedStateObservers() {
  stoppedStateObservers.forEach(observer => {
    try {
      observer(areCircuitBreakersStopped);
    } catch (error) {
      debug.error(config, 'Erreur dans un observateur de stopped state:', error);
    }
  });
}

/**
 * Initialiser les circuit breakers pour toutes les routes surveillées
 */
export function initializeCircuitBreakers() {
  areCircuitBreakersStopped = false; // Marquer comme relancés
  debug.log(config, 'Initialisation des circuit breakers...');
  
  // Marquer les circuit breakers comme initialisés
  circuitBreakersInitialized = true;

  // Ajouter les listeners de nettoyage automatique
  setupAutomaticCleanup();

  // Construire les routes à partir de la configuration centralisée
  const routes = ROUTES_CONFIG.map(routeConfig => ({
    route: routeConfig.route,
    config: routeConfig.circuitBreakerConfig
  }));

  routes.forEach(({ route, config: routeConfig }) => {
    getCircuitBreaker(route, routeConfig);
  });

  debug.log(config, `${routes.length} circuit breakers initialisés`);
  
  // Déclencher un health check USER pour tous les services après initialisation
  setTimeout(async () => {
    debug.log(config, 'Déclenchement des health checks USER post-initialisation...');
    
    for (const { route } of routes) {
      try {
        const breaker = getCircuitBreaker(route);
        if (breaker) {
          debug.log(config, `Health check USER pour ${route}`);
          await breaker.checkHealth(HEALTHCHECK_MODE.USER);
        }
      } catch (error) {
        debug.error(config, `Erreur lors du health check USER pour ${route}:`, error);
      }
    }
    
    debug.log(config, 'Health checks USER post-initialisation terminés');
    
    // Notifier les observateurs que les circuit-breakers sont relancés
    notifyStoppedStateObservers();
  }, 2000); // Délai de 2 secondes pour laisser le temps aux circuit breakers de s'initialiser complètement
}

/**
 * Arrêter tous les circuit breakers
 */
export function stopAllCircuitBreakers() {
  debug.log(config, 'Arrêt de tous les circuit breakers...');
  
  areCircuitBreakersStopped = true; // Marquer comme arrêtés
  
  circuitBreakers.forEach(breaker => {
    breaker.stopHealthMonitoring();
    breaker.notifyListeners(); // Notifier les listeners du changement d'état
  });
  
  circuitBreakers.clear();
  circuitBreakersInitialized = false; // Réinitialiser le flag
  
  // Notifier les observateurs du changement d'état
  notifyStoppedStateObservers();
  
  debug.log(config, 'Tous les circuit breakers arrêtés');
}

/**
 * Obtenir le statut de tous les circuit breakers
 */
export function getAllCircuitBreakerStatus() {
  const status = {};
  circuitBreakers.forEach((breaker, route) => {
    status[route] = breaker.getStatus();
  });
  return status;
}

/**
 * Vérifier l'état de santé d'un service spécifique
 * Gère trois niveaux d'erreur:
 * 1. Le worker n'est pas accessible (problème de connectivité)
 * 2. Le worker est accessible mais répond avec une erreur (service inaccessible)
 * 3. Les credentials sont refusés (validation échouée)
 */
export async function checkServiceHealth(route) {
  const breaker = getCircuitBreaker(route);
  
  if (!breaker) {
    return {
      success: false,
      errorType: 'input',
      message: `Circuit breaker not found for route: ${route}`
    };
  }

  const status = breaker.getStatus();
  
  if (status.isHealthy) {
    return {
      success: true,
      state: status.state,
      lastHealthCheck: status.lastHealthCheck
    };
  } else {
    return {
      success: false,
      errorType: status.errorType || 'unknown',
      message: status.errorMessage || 'Service is unhealthy',
      state: status.state,
      failures: status.failures,
      lastHealthCheck: status.lastHealthCheck
    };
  }
}

/**
 * Configurer le nettoyage automatique des circuit breakers
 */
function setupAutomaticCleanup() {
  // Flag pour éviter plusieurs enregistrements
  if (window.circuitBreakerCleanupConfigured) {
    return;
  }
  
  // Fonction de nettoyage
  const cleanup = (reason = 'unknown') => {
    if (circuitBreakers.size > 0) {
      debug.log(config, `Nettoyage automatique des circuit breakers (${reason})...`);
      stopAllCircuitBreakers();
    }
  };

  // Fonction de redémarrage des circuit breakers
  const restart = (reason = 'unknown') => {
    debug.log(config, `Redémarrage des circuit breakers (${reason})...`);
    initializeCircuitBreakers();
  };

  // Événement beforeunload : page sur le point d'être fermée/rechargée
  window.addEventListener('beforeunload', () => cleanup('beforeunload'));
  
  // Événement pagehide : page cachée/fermée (plus fiable sur mobile)
  window.addEventListener('pagehide', () => cleanup('pagehide'));
  
  // Timer pour gérer la visibilité
  let visibilityTimeout = null;

  // Événement visibilitychange : page devient invisible ou visible
  document.addEventListener('visibilitychange', () => {
    // Nettoyer le timeout précédent s'il existe
    if (visibilityTimeout) {
      clearTimeout(visibilityTimeout);
      visibilityTimeout = null;
    }

    if (document.visibilityState === 'hidden') {
      // On nettoie seulement si la page reste cachée pendant plus de 5 secondes
      // (pour éviter de nettoyer lors d'un simple changement d'onglet temporaire)
      visibilityTimeout = setTimeout(() => {
        if (document.visibilityState === 'hidden') {
          cleanup('page hidden for 5s');
        }
        visibilityTimeout = null;
      }, 5000);
    } else if (document.visibilityState === 'visible') {
      // Page redevient visible : redémarrer les circuit breakers
      restart('page visible again');
    }
  });

  // Spécial développement : écouter les hot reloads de Vite
  if (import.meta.hot) {
    import.meta.hot.on('vite:beforeUpdate', () => {
      cleanup('vite hot reload');
    });
  }

  window.circuitBreakerCleanupConfigured = true;
  debug.log(config, 'Nettoyage automatique des circuit breakers configuré');
}

/**
 * Vérifier le watchdog pour une route donnée
 * Retourne true si la requête doit être bloquée
 */
export function checkWatchdog(route) {
  const breaker = getCircuitBreaker(route);
  
  if (!breaker) {
    return false; // Pas de circuit breaker = pas de blocage
  }
  
  return breaker.recordCall();
}

/**
 * Vérifier le statut du watchdog sans enregistrer un appel
 * Utilisé pour les health checks automatiques
 */
export function isWatchdogActive(route) {
  const breaker = getCircuitBreaker(route);
  
  if (!breaker) {
    return false; // Pas de circuit breaker = pas de blocage
  }
  
  return breaker.isWatchdogActive();
}

/**
 * Réinitialiser le watchdog pour une route donnée
 * Utile pour les tests ou en cas de besoin manuel
 */
export function resetWatchdog(route) {
  const breaker = getCircuitBreaker(route);
  
  if (breaker) {
    breaker.callHistory = [];
    breaker.watchdogTriggered = false;
    breaker.lastWatchdogTrigger = null;
    debug.log(config, `Watchdog réinitialisé pour ${route}`);
  }
}

// Re-export des constantes pour faciliter l'importation
export { CIRCUIT_STATES };
